<!--
 * 文件名：LabelToolEmbed.vue
 * 描述：标注工具嵌入组件（v26.3.3 数据标注）。
 *       封装 micro-app（@micro-zoe/micro-app 1.0.0-rc.32，heart2 同款），
 *       按 toolType 注册策略统一管理子应用的 name/url/沙箱档位/data 通道。
 *       Label Studio（Django+React，不可改造）走 iframe 沙箱档；未来可改造的内部工具走 with-sandbox。
 *       生产验证结论来源：D:\CSBR\FBT-project\micro-app-demo\（Vue2 基座 + React 子应用全链路实测）。
 *
 * 使用注意（demo 实证坑，勿踩）：
 *  1. 不得给 micro-app 加 keep-alive 属性（09-24 实测白屏）：宿主页（labelToolDetail/labelExecution）销毁后
 *     保活实例残留，同 name 重挂成空壳——首进 LS 正常、返回列表换任务再进白屏。已去掉该属性：
 *     每次挂载全新加载（行为等同首次进入），代价=每次进 LS 冷加载。同理 url 必须一次性赋值
 *     （初始为空→加载后固定），动态切换会踩同款空壳，须改用 reload()（整页刷新兜底）。
 *  2. token 双通道下发：①URL 拼 token（对齐 fe buildLink，LS 的 URL token 通道独立于 postMessage 白名单，fe 实证可跑通）；
 *     ②data 内存通道冗余下发（microData）。micro-app 路由同步会把子应用路径回写主应用 query——
 *     拼 URL 的 token 会随 LS 内部路由跳转被子应用自身处理，注意勿在主应用日志/埋点里泄露。
 *  3. 基座侧动态推送必须用 setData() 方法（内部用 micro-app 模块实例；
 *     window.microApp 是注入子应用的全局，基座侧不存在）。
 *  4. URL 参数族对齐 fe-csbr-data-labeling buildLink：task/mode/isView/stageRuleCode/rectify 由 props 注入，
 *     组件负责拼装与去重（调用方 url 已含同名参数时不重复追加）；mode=review-readonly 自动带 isView=true。
-->
<template>
  <div class="label-tool-embed">
    <micro-app
      v-if="url && toolConf"
      :name="toolConf.name"      :url="targetUrl"
      :iframe="toolConf.iframe"
      :default-page="microPage"
      :disable-sandbox="toolConf.disableSandbox"
      :data="microData"
      class="label-tool-iframe"
      @datachange="onDataChange"
      @mounted="onMounted"
      @error="onError"
    ></micro-app>
    <div v-else-if="!toolConf" class="embed-tip">未注册的标注工具类型：{{ toolType }}</div>
    <div v-else class="embed-tip">未配置标注页地址</div>
  </div>
</template>

<script>
// 基座侧 API 必须用模块实例（见文件头注意事项 3）
import microApp from "@micro-zoe/micro-app";

/**
 * 标注工具注册表：新工具接入时在此登记。
 * name：micro-app 子应用名（全局唯一，setData/事件按此路由）
 * iframe：沙箱档位——true=iframe 沙箱档（子应用不可改造/跨域），false=with-sandbox（子应用可改造）
 * appendNoNavbar：是否自动追加 LS 免导航参数（no-navbar=true 隐藏子应用自身导航）
 */
const TOOL_REGISTRY = {
  // Label Studio 公司 fork（csbr-label-studio，develop-v2.0 分支）
  // 沙箱档=iframe（LS 不可改造跨域子应用）；publicPath 已在 LS 侧根治（webpack "auto"→"/react-app/"，
  //   09-11 已发版），无需关沙箱；disableSandbox 实测反致脚本不执行（页面空壳），保持沙箱开启。
  labelStudio: {
    name: "label-studio",
    iframe: true,
    disableSandbox: false,
    appendNoNavbar: true,
    appendToken: true,
  },
};

export default {
  name: "LabelToolEmbed",
  props: {
    /** 标注工具类型（TOOL_REGISTRY 的 key，当前仅 labelStudio） */
    toolType: {
      type: String,
      default: "labelStudio",
    },
    /** 子应用完整地址（LS=项目页地址，由调用方/后端配置给出，组件负责补 no-navbar 等参数） */
    url: {
      type: String,
      default: "",
    },
    /** LS 目标任务 id（对齐 fe buildLink 的 task= 参数；可空） */
    taskId: {
      type: [String, Number],
      default: "",
    },
    /** LS 页面模式（对齐 fe buildLink 的 mode=：review/qc/accept 驱动 LS ReviewControls；标注页留空） */
    mode: {
      type: String,
      default: "",
    },
    /** 是否只读（对齐 fe buildLink 的 isView=；review-readonly 模式自动置 true） */
    isView: {
      type: [Boolean, String],
      default: false,
    },
    /** 阶段规则编码（对齐 fe buildLink 的 stageRuleCode=；审核/质检/验收页按阶段配置传入） */
    stageRuleCode: {
      type: String,
      default: "",
    },
    /** 待整改嵌入（FBT mode=rectify）：LS 侧据此把任务流切到"被驳回条目"队列（rectify=true 参数） */
    rectify: {
      type: [Boolean, String],
      default: false,
    },
    /** 额外下发数据（targetRoute 等，原样透传；token 组件统一注入） */
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    toolConf() {
      return TOOL_REGISTRY[this.toolType];
    },
    /**
     * micro-app iframe 沙箱档的路由事实（2026-09-23 浏览器实证）：
     * 子应用 window.location 初始=基座地址、search 为空——url 属性的 path 与 query 只用于资源 fetch，
     * 不会到达子应用路由。LS 侧 ReviewControls/DataStore 从 location.search 读 task/mode/isView/stageRuleCode，
     * 不设置初始路由则全部落空（审核控件不出现、排序规则不生效、任务定位失效）。
     * 解法：default-page 把 LS 完整路径（path+query）设为子应用初始 location（框架原生支持，
     * 挂载时经 initRouteState→updateMicroLocation 写入并回写基座 label-studio= 键），等效于 fe 直开完整 URL。
     */
    microPage() {
      if (!this.targetUrl) return "";
      const schemeIdx = this.targetUrl.indexOf("://");
      const pathIdx = this.targetUrl.indexOf("/", schemeIdx > -1 ? schemeIdx + 3 : 0);
      return pathIdx > -1 ? this.targetUrl.slice(pathIdx) : this.targetUrl;
    },
    targetUrl() {
      if (!this.url) return "";
      // 联调开关 ls-host-override：把子应用地址 origin 覆写为本地 LS dev（如 http://localhost:8010）。
      // 不设置零行为变化（label-url 后端契约不动，仅本机嵌入地址覆写，父子本地联调用）
      const override = localStorage.getItem("ls-host-override");
      const base = override && /^https?:\/\/[^/]+$/.test(override)
        ? this.url.replace(/^https?:\/\/[^/]+/, override.replace(/\/+$/, ""))
        : this.url;
      const params = [];
      if (this.taskId !== "" && this.taskId !== null && this.taskId !== undefined) {
        params.push("task=" + encodeURIComponent(String(this.taskId)));
      }
      if (this.toolConf.appendNoNavbar && base.indexOf("no-navbar=") === -1) {
        params.push("no-navbar=true");
      }
      if (this.mode && base.indexOf("mode=") === -1) {
        params.push("mode=" + encodeURIComponent(this.mode));
      }
      // isView 恒拼（对齐 fe buildLink：isView=true/false 都拼，LS 侧按此区分只读态）；
      // review-readonly 与显式 isView 统一落成 true，其余落 false
      const isView = this.isView === true || this.isView === "true" || this.mode === "review-readonly";
      if (base.indexOf("isView=") === -1) {
        params.push("isView=" + (isView ? "true" : "false"));
      }
      if (this.stageRuleCode && base.indexOf("stageRuleCode=") === -1) {
        params.push("stageRuleCode=" + encodeURIComponent(this.stageRuleCode));
      }
      // 待整改（FBT mode=rectify）：LS 侧任务流切"被驳回条目"队列（打回条目停在审核格 rejected，
      // 按 labeling 格过滤取不到下一条）；审核/正常标注页不拼，互不影响
      if ((this.rectify === true || this.rectify === "true") && base.indexOf("rectify=") === -1) {
        params.push("rectify=true");
      }
      if (!params.length) return base;
      return base + (base.indexOf("?") > -1 ? "&" : "?") + params.join("&");
    },
    microData() {
      // token 经 SSO 网关透传（heart2 已验证），内存级下发；LS 侧桥接后写入 ls.oauth2.token
      return { token: localStorage.token || "", ...(this.data || {}) };
    },
  },
  watch: {
    // 预置种子：micro-app 挂载前把 token 写入 ls.oauth2.token（沙箱 iframe 与父页同源共享 localStorage，
    // 实测 LS 启动首批 API 早于 mounted 补推的送达时点——首批 401 会把路由重定向回首页，补推已来不及；
    // 预置让 LS 一启动就读到 token。无 LS 用户映射的账号会被 LS 终态清空，属预期）
    url: {
      handler(v) {
        if (v) this.seedToken();
      },
      immediate: true,
    },
  },
  methods: {
    /**
     * 预置 token 到 LS 的存储键（父页 localStorage=沙箱 iframe 同源存储）。
     */
    seedToken() {
      const token = localStorage.getItem("ls-token-override") || localStorage.token || "";
      if (!token) return;
      try {
        localStorage.setItem("ls.oauth2.token", token);
        sessionStorage.setItem("ls.oauth2.token", token);
      } catch (e) {
        /* 存储不可用时靠 mounted 补推兜底 */
      }
    },
    /**
     * 基座→子应用动态推送（token 自动携带，payload 可带 targetRoute 驱动子应用内部路由）。
     */
    setData(payload) {
      if (!this.toolConf) return;
      microApp.setData(this.toolConf.name, {
        token: localStorage.getItem("ls-token-override") || localStorage.token || "",
        ...(payload || {}),
        ts: Date.now(),
      });
    },
    /**
     * 强制重载子应用。iframe 沙箱档无法 v-if 原地重挂（空壳坑），只能整页刷新。
     */
    reload() {
      window.location.reload();
    },
    /** 子→基通信：子应用 window.microApp.dispatch 的数据 */
    /** 子→基通信：子应用 window.microApp.dispatch 的数据（micro-app 标准通道） */
    onDataChange(e) {
      const data = (e.detail && e.detail.data) || {};
      // 提交成功等业务事件由标准通道转发（新版 LS dispatch→datachange）；
      // 旧版 LS / fe iframe 场景走 postMessage→onLsMessage 兜底——LS 侧二选一发送，同一宿主只收一种，无重复
      if (data.type === "UPDATE_SUCCESS") {
        this.$emit("submit-success", data);
        return;
      }
      this.$emit("datachange", data);
    },
    /**
     * LS 提交成功闭环（D3，对齐旧线 fe dataLabelSetting 的 UPDATE_SUCCESS 处理）：
     * LS 提交后 window.parent.postMessage({type:'UPDATE_SUCCESS'})（lsf-sdk.js 同款），
     * iframe 档下 parent=基座 window，在此监听并透传 submit-success 事件给宿主页面。
     * 10-09 追加 TASK_CHANGED（LS selectTask 切条目）：postMessage 兜底通道 → 统一透传 datachange，
     * 与 micro-app dispatch 通道同款（重复到达由宿主按 taskId 去重）。
     */
    onLsMessage(e) {
      const d = e && e.data;
      if (d && d.type === "UPDATE_SUCCESS") {
        this.$emit("submit-success", d);
      } else if (d && d.type === "TASK_CHANGED") {
        this.$emit("datachange", d);
      }
    },
    onMounted() {
      this.$emit("mounted");
      // 初始 :data 在 iframe 沙箱档挂载竞态下可能未送达子应用（09-17 FBT labelExecution 实测：
      // 挂载后 ls.oauth2.token 恒空、API 全 401，手动 setData 立即送达），mounted 后补推一次 token。
      this.setData({});
      // 二次保险：LS 侧入口脚本注册 addDataListener 早于 mounted 事件，但保底 2s 再推一次（幂等覆写）
      setTimeout(() => this.setData({}), 2000);
    },
    onError(e) {
      this.$emit("error", (e.detail && e.detail.data) || {});
    },
  },
  mounted() {
    window.addEventListener("message", this.onLsMessage);
    // token 保活重种（09-24"不停刷新"根因兜底）：LS api-proxy 对 401 会 storeAccessToken(null) 清掉
    // ls.oauth2.token，清后每请求触发换发（FBT 场景换发必失败）=请求风暴/页面不停刷新；
    // LS 每请求现读 storage（无内存缓存），定期重种宿主 token 即可把被清的 token 补回
    this.tokenKeepAlive = setInterval(this.seedToken, 60000);
  },
  beforeDestroy() {
    clearInterval(this.tokenKeepAlive);
    window.removeEventListener("message", this.onLsMessage);
  },
};
</script>

<style scoped>
.label-tool-embed {
  width: 100%;
  height: 100%;
  min-height: 400px;
  text-align: left; /* 抵御全局 #app text-align:center 波及 */
}
.label-tool-iframe {
  display: block;
  width: 100%;
  height: 100%;
}
.embed-tip {
  padding: 40px 0;
  text-align: center;
  color: #909399;
  font-size: 13px;
}
</style>
